/**
 * Plan del completivo del inicial: el saldo del inicial pactado (completivo)
 * repartido en partes con fecha. El plan es solo el compromiso de fechas; el
 * dinero cobrado sigue entrando en la tabla `inicial`, así que lo pagado de
 * cada parte se DERIVA aplicando lo abonado a las partes por orden de fecha.
 *
 * Contrato con el backend:
 *  - Venta (/api/ventaproducto_guardar): `completivoplan: [{ fecha, monto }]`.
 *    Vacío = una sola parte con la fecha de la venta (todo exigible de una vez,
 *    como antes del plan).
 *  - Cobro (/api/recuperartablaamortizacion): `completivoplan: [{ fecha, monto }]`
 *    y `completivototal` (pendiente total). Sin plan, `completivo` es el total.
 */

export const FRECUENCIAS_COMPLETIVO = [
  { value: "Dias", label: "Cada día" },
  { value: "Semanas", label: "Cada semana" },
  { value: "Quincenas", label: "Cada quincena" },
  { value: "Meses", label: "Cada mes" },
];

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export function hoyISO() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// Suma un período a una fecha ISO. Meses respeta el día del mes de la fecha
// base (31 → fin de mes en los meses cortos), para que no se corra.
export function sumarPeriodo(fechaISO, frecuencia, veces = 1) {
  const [y, m, d] = String(fechaISO)
    .split("-")
    .map((x) => parseInt(x, 10));
  if (!y || !m || !d) return "";
  let dt;
  if (frecuencia === "Meses") {
    const destino = new Date(y, m - 1 + veces, 1);
    const ultimo = new Date(destino.getFullYear(), destino.getMonth() + 1, 0).getDate();
    dt = new Date(destino.getFullYear(), destino.getMonth(), Math.min(d, ultimo));
  } else {
    const dias = { Dias: 1, Quincenas: 15 }[frecuencia] || 7;
    dt = new Date(y, m - 1, d + dias * veces);
  }
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${mm}-${dd}`;
}

// Reparte el completivo en `partes` iguales al peso; los centavos y el
// redondeo van a la última parte para que la suma cuadre exacta.
export function repartirCompletivo(completivo, partes, primeraFecha, frecuencia) {
  const total = r2(completivo);
  const n = Math.max(1, Math.floor(Number(partes) || 1));
  if (total <= 0 || !primeraFecha) return [];
  const base = Math.floor(total / n);
  const plan = [];
  for (let i = 0; i < n; i += 1) {
    const monto = i === n - 1 ? r2(total - base * (n - 1)) : base;
    plan.push({ fecha: sumarPeriodo(primeraFecha, frecuencia, i), monto });
  }
  return plan;
}

export function sumaPlan(plan) {
  return r2((plan || []).reduce((a, p) => a + (Number(p.monto) || 0), 0));
}

// Error de validación del plan para guardar la venta, o null si está bien.
// Un plan vacío es válido: el backend guarda una sola parte con fecha de hoy.
export function validarPlanCompletivo(plan, completivo, hoy = hoyISO()) {
  if (!plan || !plan.length) return null;
  if (r2(completivo) <= 0) return null;
  for (let i = 0; i < plan.length; i += 1) {
    const p = plan[i];
    if (!p.fecha) return `Plan del completivo: indique la fecha del pago ${i + 1}`;
    if (p.fecha < hoy) return `Plan del completivo: el pago ${i + 1} tiene una fecha pasada`;
    if (!(Number(p.monto) > 0))
      return `Plan del completivo: el monto del pago ${i + 1} debe ser mayor que 0`;
  }
  const dif = r2(r2(completivo) - sumaPlan(plan));
  if (Math.abs(dif) >= 0.01) {
    const abs = Math.abs(dif).toLocaleString("es-DO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `Plan del completivo: ${
      dif > 0 ? "faltan" : "sobran"
    } RD$ ${abs} para cuadrar con el completivo`;
  }
  return null;
}

// Plan ordenado por fecha y con montos numéricos, listo para el payload.
export function normalizarPlan(plan) {
  return [...(plan || [])]
    .map((p) => ({ fecha: p.fecha, monto: r2(p.monto) }))
    .sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)));
}

/**
 * Estado del plan a una fecha: aplica lo ya pagado (suma del plan menos el
 * pendiente total) a las partes por orden de fecha.
 * Devuelve { partes: [{ nro, fecha, monto, pagado, pendiente, estado }],
 *            vencido, porVencer, proximo } — estado: pagada | vencida | porvencer.
 */
export function estadoPlanCompletivo(plan, pendienteTotal, hoy = hoyISO()) {
  const orden = normalizarPlan(plan);
  const pendiente = r2(pendienteTotal);
  let pagado = Math.max(0, r2(sumaPlan(orden) - pendiente));
  let vencido = 0;
  let porVencer = 0;
  let proximo = null;
  const partes = orden.map((p, i) => {
    const aplicado = r2(Math.min(p.monto, pagado));
    pagado = r2(pagado - aplicado);
    const resta = r2(p.monto - aplicado);
    let estado = "pagada";
    if (resta >= 0.01) {
      if (p.fecha <= hoy) {
        estado = "vencida";
        vencido += resta;
      } else {
        estado = "porvencer";
        porVencer += resta;
        if (!proximo) proximo = p.fecha;
      }
    }
    return {
      nro: i + 1,
      fecha: p.fecha,
      monto: p.monto,
      pagado: aplicado,
      pendiente: resta,
      estado,
    };
  });
  return { partes, vencido: r2(vencido), porVencer: r2(porVencer), proximo };
}

export function fechaCorta(iso) {
  const [y, m, d] = String(iso || "").split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
}
